import React, { useState, useCallback, useEffect, useRef } from 'react';
import {
  Card,
  CardContent,
  Box,
  Typography,
  IconButton,
  Switch,
  FormControlLabel,
  Collapse,
  Stack,
  Chip,
  Button,
  Alert,
  CircularProgress,
  Tooltip,
} from '@mui/material';
import {
  Delete as DeleteIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  DragIndicator as DragIcon,
  Save as SaveIcon,
  Edit as EditIcon,
  Error as ErrorIcon,
} from '@mui/icons-material';
import { QuestionDTO, UpdateQuestionDTO } from '../dtos';
import { QuestionType, QUESTION_TYPE_CONFIGS, ValidationRules } from '../types';
import { MultiLanguageInput } from './MultiLanguageInput';
import { QuestionTypeDisplay } from './QuestionTypeDisplay';
import { QuestionOptionsEditor } from './QuestionOptionsEditor';
import { QuestionRangeEditor } from './QuestionRangeEditor';
import { QuestionPreview } from './QuestionPreview';
import {
  generateA11yId,
  LiveAnnouncer,
  A11Y_MESSAGES,
  usePrefersReducedMotion,
} from '../utils/accessibility';

interface QuestionCardProps {
  question: QuestionDTO;
  index: number;
  onUpdate: (questionId: string, data: UpdateQuestionDTO) => Promise<void>;
  onDelete: (questionId: string) => Promise<void>;
  isUpdating?: boolean;
  isDragDisabled?: boolean;
  dragHandleProps?: any;
  accessibilityProps?: Record<string, any>;
  isFocused?: boolean;
  onFocus?: () => void;
  onBlur?: () => void;
  isEditDisabled?: boolean; // For template questions
  isDeleteDisabled?: boolean; // For template questions
}

export const QuestionCard: React.FC<QuestionCardProps> = ({
  question,
  index,
  onUpdate,
  onDelete,
  isUpdating: _isUpdating = false,
  isDragDisabled = false,
  dragHandleProps,
  accessibilityProps = {},
  isFocused = false,
  onFocus,
  onBlur,
  isEditDisabled = false,
  isDeleteDisabled = false,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editData, setEditData] = useState<UpdateQuestionDTO>({});
  const [validationErrors, setValidationErrors] = useState<string[]>([]);

  // Keep track of the question ID to detect if we're dealing with the same question
  const questionIdRef = useRef(question.id);
  const isEditingRef = useRef(isEditing);

  // Update refs when state changes
  useEffect(() => {
    isEditingRef.current = isEditing;
  }, [isEditing]);

  // Protect editing state when question changes
  useEffect(() => {
    const prevQuestionId = questionIdRef.current;
    const currentQuestionId = question.id;

    // If this is a completely different question, reset all state
    if (prevQuestionId && prevQuestionId !== currentQuestionId) {
      questionIdRef.current = currentQuestionId;

      // Only reset if we're not in the middle of editing
      // This prevents the race condition when clicking edit on a newly created question
      if (!isEditingRef.current) {
        setIsEditing(false);
        setEditData({});
        setValidationErrors([]);
        setIsExpanded(false);
      }
    } else if (!prevQuestionId) {
      // Initial mount - just set the ref, don't reset anything
      questionIdRef.current = currentQuestionId;
    }
    // If IDs match, this is just an update to the same question - do nothing
  }, [question.id]);

  // Get question type configuration
  const typeConfig = QUESTION_TYPE_CONFIGS.find((config) => config.type === question.type);

  // Accessibility features
  const prefersReducedMotion = usePrefersReducedMotion();
  const announcer = LiveAnnouncer.getInstance();
  const questionId = generateA11yId(`question-${question.id}`);
  const helpId = generateA11yId(`question-help-${question.id}`);
  const errorsId = generateA11yId(`question-errors-${question.id}`);

  // Manual-only save - no auto-save functionality
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const resetEditState = () => {
    setEditData({});
    setValidationErrors([]);
    setHasUnsavedChanges(false);
    setIsEditing(false);
    isEditingRef.current = false; // Update ref synchronously
  };

  const startEditing = () => {
    if (isEditDisabled) {
      announcer.announce('This question cannot be edited as it comes from a template.');
      return;
    }

    const initialEditData = {
      title: question.title,
      description: question.description,
      isRequired: question.isRequired,
      hasCommentField: question.hasCommentField,
      commentFieldDescription: question.commentFieldDescription,
      options: question.options,
      rangeConfig: question.rangeConfig,
      validationRules: question.validationRules,
    };

    setEditData(initialEditData);
    setIsEditing(true);
    isEditingRef.current = true; // Update ref synchronously to prevent race condition
    setIsExpanded(true);
    announcer.announce(A11Y_MESSAGES.EDITING_STARTED);
  };

  const validateData = useCallback(() => {
    const errors: string[] = [];
    const data = editData;

    // Title validation
    if (!data.title?.en?.trim()) {
      errors.push('English title is required');
    }

    // Type-specific validation (using immutable question.type)
    switch (question.type) {
      case QuestionType.SINGLE_SELECT:
      case QuestionType.MULTI_SELECT:
        if (!data.options || data.options.length < 2) {
          errors.push('At least 2 options are required');
        }
        data.options?.forEach((option, index) => {
          if (!option.label?.en?.trim()) {
            errors.push(`Option ${index + 1} must have English text`);
          }
        });
        break;

      case QuestionType.RANGE:
        if (!data.rangeConfig) {
          errors.push('Range configuration is required');
        } else if (data.rangeConfig.min >= data.rangeConfig.max) {
          errors.push('Maximum value must be greater than minimum value');
        }
        break;

      case QuestionType.NUMBER:
        if (
          data.validationRules?.minValue !== undefined &&
          data.validationRules?.maxValue !== undefined &&
          data.validationRules.minValue >= data.validationRules.maxValue
        ) {
          errors.push('Maximum value must be greater than minimum value');
        }
        break;
    }

    // Comment field validation
    if (
      data.hasCommentField &&
      data.commentFieldDescription &&
      !data.commentFieldDescription.en?.trim()
    ) {
      errors.push('Comment field description must have English text when enabled');
    }

    setValidationErrors(errors);
    return errors.length === 0;
  }, [editData, question.type]);

  const updateField = (field: keyof UpdateQuestionDTO, value: any) => {
    setEditData((prev) => ({ ...prev, [field]: value }));
    setHasUnsavedChanges(true);
    setSaveError(null);
  };

  const handleSave = async () => {
    try {
      setIsSaving(true);
      setSaveError(null);
      if (!validateData()) {
        throw new Error('Validation failed');
      }
      await onUpdate(question.id, editData);
      resetEditState(); // Manual save should close the editor
      announcer.announce(A11Y_MESSAGES.QUESTION_UPDATED);
    } catch (error) {
      console.error('Failed to save question:', error);
      setSaveError(error instanceof Error ? error.message : 'Save failed');
      announcer.announce('Failed to save question. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    resetEditState();
    announcer.announce(A11Y_MESSAGES.EDITING_CANCELLED);
  };

  const handleDelete = async () => {
    if (isDeleteDisabled) {
      announcer.announce('This question cannot be deleted as it comes from a template.');
      return;
    }

    if (window.confirm('Are you sure you want to delete this question?')) {
      try {
        await onDelete(question.id);
      } catch (error) {
        console.error('Failed to delete question:', error);
      }
    }
  };

  const handleValidationRulesChange = (rules: ValidationRules) => {
    updateField('validationRules', rules);
  };

  const renderValidationEditor = () => {
    if (!typeConfig?.supportsValidation) return null;

    const rules = editData.validationRules || question.validationRules || {};

    return (
      <Box sx={{ mt: 2 }}>
        <Typography variant="subtitle2" gutterBottom>
          Validation Rules
        </Typography>
        <Stack spacing={2}>
          {question.type === QuestionType.TEXT && (
            <>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <Box sx={{ flex: 1 }}>
                  <input
                    type="number"
                    placeholder="Min length"
                    value={rules.minLength || ''}
                    onChange={(e) =>
                      handleValidationRulesChange({
                        ...rules,
                        minLength: e.target.value ? parseInt(e.target.value) : undefined,
                      })
                    }
                    style={{
                      width: '100%',
                      padding: '8px',
                      border: '1px solid',
                      borderColor: 'var(--mui-palette-divider)',
                      borderRadius: '4px',
                      backgroundColor: 'var(--mui-palette-background-paper)',
                      color: 'var(--mui-palette-text-primary)',
                    }}
                  />
                </Box>
                <Box sx={{ flex: 1 }}>
                  <input
                    type="number"
                    placeholder="Max length"
                    value={rules.maxLength || ''}
                    onChange={(e) =>
                      handleValidationRulesChange({
                        ...rules,
                        maxLength: e.target.value ? parseInt(e.target.value) : undefined,
                      })
                    }
                    style={{
                      width: '100%',
                      padding: '8px',
                      border: '1px solid',
                      borderColor: 'var(--mui-palette-divider)',
                      borderRadius: '4px',
                      backgroundColor: 'var(--mui-palette-background-paper)',
                      color: 'var(--mui-palette-text-primary)',
                    }}
                  />
                </Box>
              </Box>
            </>
          )}
          {question.type === QuestionType.NUMBER && (
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Box sx={{ flex: 1 }}>
                <input
                  type="number"
                  placeholder="Min value"
                  value={rules.minValue || ''}
                  onChange={(e) =>
                    handleValidationRulesChange({
                      ...rules,
                      minValue: e.target.value ? parseFloat(e.target.value) : undefined,
                    })
                  }
                  style={{
                    width: '100%',
                    padding: '8px',
                    border: '1px solid #ccc',
                    borderRadius: '4px',
                  }}
                />
              </Box>
              <Box sx={{ flex: 1 }}>
                <input
                  type="number"
                  placeholder="Max value"
                  value={rules.maxValue || ''}
                  onChange={(e) =>
                    handleValidationRulesChange({
                      ...rules,
                      maxValue: e.target.value ? parseFloat(e.target.value) : undefined,
                    })
                  }
                  style={{
                    width: '100%',
                    padding: '8px',
                    border: '1px solid #ccc',
                    borderRadius: '4px',
                  }}
                />
              </Box>
            </Box>
          )}
        </Stack>
      </Box>
    );
  };

  const currentTitle = isEditing ? editData.title || question.title : question.title;
  const currentIsRequired = isEditing
    ? (editData.isRequired ?? question.isRequired)
    : question.isRequired;

  return (
    <Card
      {...accessibilityProps}
      id={questionId}
      role="article"
      aria-labelledby={`${questionId}-title`}
      aria-describedby={validationErrors.length > 0 ? errorsId : helpId}
      tabIndex={isFocused ? 0 : -1}
      onFocus={onFocus}
      onBlur={onBlur}
      sx={{
        mb: 2,
        border: '1px solid',
        borderColor: 'divider',
        position: 'relative',
        overflow: 'visible', // Allow drag handle to overflow
        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.04)',
        '&:focus': {
          outline: 'none',
        },
        transition: prefersReducedMotion ? 'none' : 'all 0.2s ease',
      }}
    >
      {/* Drag Handle */}
      {!isDragDisabled && (
        <Box
          {...(!isEditing ? dragHandleProps : {})}
          sx={{
            position: 'absolute',
            left: -10,
            top: '50%',
            transform: 'translateY(-50%)',
            backgroundColor: 'action.hover',
            borderRadius: '3px',
            p: 0.25,
            cursor: isEditing ? 'not-allowed' : 'grab',
            zIndex: 10,
            border: '1px solid',
            borderColor: 'divider',
            width: 20,
            height: 35,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            opacity: isEditing ? 0.4 : 1,
            '&:active': { cursor: isEditing ? 'not-allowed' : 'grabbing' },
            '&:hover': isEditing ? {} : { backgroundColor: 'action.selected' },
          }}
        >
          <DragIcon sx={{ fontSize: 14 }} />
        </Box>
      )}

      <CardContent
        sx={{
          ml: !isDragDisabled ? 1 : 0,
          transition: prefersReducedMotion ? 'none' : 'margin 0.2s ease',
        }}
      >
        {/* Header */}
        <Box
          sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 2 }}
        >
          <Stack direction="row" spacing={1} alignItems="center">
            <Chip label={`Q${index + 1}`} size="small" color="primary" variant="outlined" />
            <Typography variant="subtitle2" color="text.secondary">
              {typeConfig?.label || question.type}
            </Typography>
            {currentIsRequired && (
              <Chip label="Required" size="small" color="error" variant="outlined" />
            )}

            {/* Save status indicators */}
            {isEditing && (
              <>
                {saveError && (
                  <Tooltip title={`Save failed: ${saveError}`}>
                    <Chip
                      label="Error"
                      size="small"
                      color="error"
                      variant="outlined"
                      icon={<ErrorIcon />}
                    />
                  </Tooltip>
                )}
                {hasUnsavedChanges && (
                  <Chip label="Unsaved" size="small" color="warning" variant="outlined" />
                )}
              </>
            )}
          </Stack>

          <Stack direction="row" spacing={1}>
            {isEditing ? (
              <>
                <Button size="small" onClick={handleCancel} disabled={isSaving}>
                  Cancel
                </Button>
                <Button
                  size="small"
                  variant="contained"
                  onClick={handleSave}
                  disabled={isSaving || validationErrors.length > 0}
                  startIcon={isSaving ? <CircularProgress size={16} /> : <SaveIcon />}
                >
                  {isSaving ? 'Saving...' : 'Save & Close'}
                </Button>
              </>
            ) : (
              <>
                <IconButton size="small" onClick={() => setIsExpanded(!isExpanded)}>
                  {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                </IconButton>
                {!isEditDisabled && (
                  <Tooltip title="Edit question">
                    <IconButton size="small" onClick={startEditing}>
                      <EditIcon />
                    </IconButton>
                  </Tooltip>
                )}
                {!isDeleteDisabled && (
                  <Tooltip title="Delete question">
                    <IconButton size="small" color="error" onClick={handleDelete}>
                      <DeleteIcon />
                    </IconButton>
                  </Tooltip>
                )}
              </>
            )}
          </Stack>
        </Box>

        {/* Title Display (hidden when editing) */}
        {!isEditing && (
          <Typography
            variant="h6"
            id={`${questionId}-title`}
            component="h3"
            sx={{ mb: 1, wordBreak: 'break-word' }}
          >
            {currentTitle?.en || 'Untitled Question'}
          </Typography>
        )}

        {/* Validation Errors */}
        {validationErrors.length > 0 && (
          <Alert severity="error" sx={{ mb: 2 }} id={errorsId} role="alert" aria-live="polite">
            <ul style={{ margin: 0, paddingLeft: 20 }}>
              {validationErrors.map((error, i) => (
                <li key={i}>{error}</li>
              ))}
            </ul>
          </Alert>
        )}

        {/* Accessibility help text */}
        <Typography
          id={helpId}
          variant="body2"
          component="div"
          sx={{
            position: 'absolute',
            left: '-10000px',
            width: '1px',
            height: '1px',
            overflow: 'hidden',
          }}
        >
          Question {index + 1}: {question.title.en || 'Untitled'}.
          {question.isRequired ? 'Required question. ' : 'Optional question. '}
          Type: {typeConfig?.label || question.type}.
          {isEditing ? 'Currently editing. ' : 'Press Enter to edit. '}
          Use arrow keys to navigate between questions.
        </Typography>

        {/* Expanded Content */}
        <Collapse in={isExpanded || isEditing}>
          <Box sx={{ mt: 2 }}>
            {isEditing ? (
              <Stack spacing={3}>
                {/* Question Type Display (Read-only) */}
                <QuestionTypeDisplay type={question.type} />

                {/* Title Editor */}
                <MultiLanguageInput
                  label="Question Title"
                  value={editData.title || question.title}
                  onChange={(title) => updateField('title', title)}
                  required
                  error={!editData.title?.en?.trim()}
                />

                {/* Description Editor */}
                <MultiLanguageInput
                  label="Description (Optional)"
                  value={editData.description || question.description || {}}
                  onChange={(description) => updateField('description', description)}
                  multiline
                  rows={3}
                />

                {/* Question Type Specific Editors */}
                {(question.type === QuestionType.SINGLE_SELECT ||
                  question.type === QuestionType.MULTI_SELECT) && (
                  <QuestionOptionsEditor
                    options={editData.options || question.options || []}
                    onChange={(options) => updateField('options', options)}
                    questionType={question.type as 'SINGLE_SELECT' | 'MULTI_SELECT'}
                  />
                )}

                {question.type === QuestionType.RANGE && (
                  <QuestionRangeEditor
                    rangeConfig={
                      editData.rangeConfig ||
                      question.rangeConfig || { min: 1, max: 5, displayType: 'scale' }
                    }
                    onChange={(rangeConfig) => updateField('rangeConfig', rangeConfig)}
                  />
                )}

                {/* Validation Rules */}
                {renderValidationEditor()}

                {/* Question Settings */}
                <Box>
                  <Stack spacing={2}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={editData.isRequired ?? question.isRequired}
                          onChange={(e) => updateField('isRequired', e.target.checked)}
                        />
                      }
                      label="Required question"
                    />

                    <FormControlLabel
                      control={
                        <Switch
                          checked={editData.hasCommentField ?? question.hasCommentField}
                          onChange={(e) => updateField('hasCommentField', e.target.checked)}
                        />
                      }
                      label="Allow additional comments"
                    />

                    {(editData.hasCommentField ?? question.hasCommentField) && (
                      <MultiLanguageInput
                        label="Comment Field Description"
                        value={
                          editData.commentFieldDescription || question.commentFieldDescription || {}
                        }
                        onChange={(description) =>
                          updateField('commentFieldDescription', description)
                        }
                        placeholder="e.g., Please provide additional details"
                        error={
                          !editData.commentFieldDescription?.en?.trim() && editData.hasCommentField
                        }
                      />
                    )}
                  </Stack>
                </Box>
              </Stack>
            ) : (
              <Stack spacing={2}>
                {/* Description */}
                {question.description?.en && (
                  <Typography variant="body2" color="text.secondary">
                    {question.description.en}
                  </Typography>
                )}

                {/* Question Preview */}
                <QuestionPreview question={question} />
              </Stack>
            )}
          </Box>
        </Collapse>

        {/* Unsaved Changes Warning */}
        {hasUnsavedChanges && (
          <Alert severity="warning" sx={{ mt: 2 }}>
            You have unsaved changes. Click &quot;Save &amp; Close&quot; to preserve your changes.
          </Alert>
        )}
      </CardContent>
    </Card>
  );
};
